import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { registerUser, loginUser } from '../services/userService';
import { useAuth } from '../Context/AuthContext';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import { GOOGLE_CLIENT_ID } from '../constants/googleAuth';
import './Login.css';

function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: (location.state?.email || '').toLowerCase(),
    password: '',
    confirmPassword: '',
    phone: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(location.state?.message || '');
  const [showPassword, setShowPassword] = useState(false);
  const startGoogleLogin = useGoogleAuth({ setError, setLoading, setSuccessMessage });

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.role === 'admin') {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'email' ? value.toLowerCase() : value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.name.trim()) {
      setError('Name is required');
      setLoading(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const userData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        ...(formData.phone.trim() && { phone: formData.phone.trim() }),
        ...(formData.address.trim() && { address: formData.address.trim() })
      };

      const result = await registerUser(userData);
      let nextUser = null;
      let token = null;

      if (result) {
        if (result.user && result.token) {
          nextUser = result.user;
          token = result.token;
        } else if (result.token && (result.id || result._id || result.email)) {
          nextUser = result;
          token = result.token;
        } else if (result.id || result._id || result.email) {
          nextUser = result;
          try {
            const loginResult = await loginUser(formData.email.trim().toLowerCase(), formData.password);
            if (loginResult.user && loginResult.token) {
              nextUser = loginResult.user;
              token = loginResult.token;
            } else if (loginResult.token) {
              token = loginResult.token;
              nextUser = loginResult.user || result;
            }
          } catch (loginErr) {
            console.error('Auto-login error:', loginErr);
            navigate('/login', {
              state: {
                message: 'Registration successful! Please login with your credentials.',
                email: formData.email.trim().toLowerCase()
              }
            });
            return;
          }
        }
      }

      if (nextUser) {
        login(nextUser, token || null);
        if (nextUser.role === 'admin') {
          navigate('/dashboard');
        } else {
          navigate('/');
        }
      } else {
        throw new Error('Registration successful but received invalid response from server');
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container signup-page">
      <div className="login-card signup-card">
        <aside
          className="login-left-panel signup-left"
          style={{ backgroundImage: "url('/assets/img/slider/starlink.jpeg')" }}
        >
          <div className="login-left-overlay" aria-hidden="true" />

          <div className="login-brand">
            <Link to="/" className="login-brand__logo-link" aria-label="Fabritech home">
              <img
                src="/assets/img/logo/F_logo.png"
                alt="Fabritech"
                className="login-brand__logo"
              />
            </Link>

            <h2 className="login-brand__title">
              Join <span>Fabritech</span> today
            </h2>
            <p className="login-brand__text">
              Create your account to shop Starlink, security, and networking — and keep orders and wishlist in one place.
            </p>

            <ul className="login-brand__points">
              <li>
                <span className="login-brand__point-icon" aria-hidden="true">
                  <i className="fa-solid fa-bolt"></i>
                </span>
                <span>
                  <strong>Faster Checkout</strong>
                  <small>Save time on your next order</small>
                </span>
              </li>
              <li>
                <span className="login-brand__point-icon" aria-hidden="true">
                  <i className="fa-solid fa-heart"></i>
                </span>
                <span>
                  <strong>Save Your Favorites</strong>
                  <small>Access your wishlist anytime</small>
                </span>
              </li>
              <li>
                <span className="login-brand__point-icon" aria-hidden="true">
                  <i className="fa-solid fa-shield-halved"></i>
                </span>
                <span>
                  <strong>Secure &amp; Easy</strong>
                  <small>Your data is always protected</small>
                </span>
              </li>
            </ul>
          </div>

          <p className="login-brand__footer">
            Quality Products <span>|</span> Reliable Solutions <span>|</span> A Connected Tomorrow
          </p>
        </aside>

        <div className="login-right-panel signup-right">
          <div className="login-form-container signup-form-container">
            <div className="login-form-top">
              <Link to="/" className="login-back">
                <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
                Back to Store
              </Link>
            </div>

            <h1 className="login-welcome">Create account</h1>
            <p className="login-subtitle">
              Sign up for Fabritech to start shopping and manage your orders.
            </p>

            <form onSubmit={handleSubmit} className="login-form signup-form" noValidate>
              {successMessage && (
                <div className="login-success-message" role="status">
                  <i className="fas fa-check-circle" aria-hidden="true"></i>
                  <span>{successMessage}</span>
                </div>
              )}

              {error && (
                <div className="login-error-message" role="alert">
                  <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
                  <span>{error}</span>
                </div>
              )}

              <div className="signup-grid">
                <div className="login-field">
                  <label className="login-field-label" htmlFor="signup-name">Full Name</label>
                  <div className="login-input-group">
                    <i className="fas fa-user login-input-icon" aria-hidden="true"></i>
                    <input
                      id="signup-name"
                      type="text"
                      name="name"
                      placeholder="Jane Doe"
                      value={formData.name}
                      onChange={handleChange}
                      className="login-input"
                      required
                      disabled={loading}
                      minLength={2}
                      maxLength={100}
                      autoComplete="name"
                    />
                  </div>
                </div>

                <div className="login-field">
                  <label className="login-field-label" htmlFor="signup-email">Email Address</label>
                  <div className="login-input-group">
                    <i className="fas fa-envelope login-input-icon" aria-hidden="true"></i>
                    <input
                      id="signup-email"
                      type="email"
                      name="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="login-input"
                      required
                      disabled={loading}
                      autoComplete="email"
                      autoCapitalize="none"
                      spellCheck={false}
                    />
                  </div>
                </div>

                <div className="login-field">
                  <label className="login-field-label" htmlFor="signup-password">Password</label>
                  <div className="login-input-group login-input-group--password">
                    <i className="fas fa-lock login-input-icon" aria-hidden="true"></i>
                    <input
                      id="signup-password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Min. 6 characters"
                      value={formData.password}
                      onChange={handleChange}
                      className="login-input"
                      required
                      minLength={6}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i>
                    </button>
                  </div>
                </div>

                <div className="login-field">
                  <label className="login-field-label" htmlFor="signup-confirm">Confirm Password</label>
                  <div className="login-input-group">
                    <i className="fas fa-lock login-input-icon" aria-hidden="true"></i>
                    <input
                      id="signup-confirm"
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      placeholder="Repeat password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="login-input"
                      required
                      minLength={6}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <div className="login-field">
                  <label className="login-field-label" htmlFor="signup-phone">Phone</label>
                  <div className="login-input-group">
                    <i className="fas fa-phone login-input-icon" aria-hidden="true"></i>
                    <input
                      id="signup-phone"
                      type="tel"
                      name="phone"
                      placeholder="Optional"
                      value={formData.phone}
                      onChange={handleChange}
                      className="login-input"
                      disabled={loading}
                      autoComplete="tel"
                      maxLength={20}
                    />
                  </div>
                </div>

                <div className="login-field">
                  <label className="login-field-label" htmlFor="signup-address">Address</label>
                  <div className="login-input-group">
                    <i className="fas fa-map-marker-alt login-input-icon" aria-hidden="true"></i>
                    <input
                      id="signup-address"
                      type="text"
                      name="address"
                      placeholder="Optional"
                      value={formData.address}
                      onChange={handleChange}
                      className="login-input"
                      disabled={loading}
                      autoComplete="street-address"
                      maxLength={200}
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    <span>Creating account...</span>
                  </>
                ) : (
                  <>
                    <span>Create Account</span>
                    <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                  </>
                )}
              </button>

              <div className="login-divider">OR</div>

              <button
                type="button"
                className="login-google"
                disabled={loading || !GOOGLE_CLIENT_ID}
                onClick={() => startGoogleLogin()}
              >
                <svg className="login-google__icon" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.5-.4-3.5z" />
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                  <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.5-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.5 5.7-6.5 7.1l.1.1 6.2 5.2C36.8 41.7 44 36 44 24c0-1.3-.1-2.5-.4-3.5z" />
                </svg>
                Continue with Google
              </button>

              <div className="login-signup">
                <span className="login-signup-text">Already have an account? </span>
                <Link to="/login" className="login-signup-link">
                  Sign in
                </Link>
              </div>

              <p className="login-secure">
                <i className="fa-solid fa-shield-halved" aria-hidden="true"></i>
                Your information is securely protected
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
